import React, { type ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  TextField,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardActionArea,
  Divider,
  Stack,
} from '@mui/material';
import { connect, type ConnectedProps } from 'react-redux';
import XHRUpload from '@uppy/xhr-upload';
import Uppy from '@uppy/core';
import { DashboardModal } from '@uppy/react';
import { API_URL } from '@/config';
import { openModal } from '@/core/features/modal/state/actions';
import { openSnackbar } from '@/core/features/snackbar';
import {
  fetchCollection,
  setNewLogo,
  setTeamLogo,
} from '@/slices/logos/actions';
import {
  selectLogosLoading,
  selectLogosError,
  selectLogoCollection,
} from '@/slices/logos/selectors';
import { type Logo } from '@/slices/logos/types';
import { type RootState } from '@/store';
import { ModalLayout } from '../../layouts';
import { type ModalContentProps } from '../../../state';
import styles from './styles';

import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';

type Props = ConnectedProps<typeof connector> & ModalContentProps;

const EditLogoModal = ({
  closeModal,
  modalData,
  fetchCollection,
  logos,
  setNewLogo,
  setTeamLogo,
  loading,
  openSnackbar,
}: Props) => {
  const { teamType, external_id, currentLogo } = modalData;

  const [newLogoUrl, setNewLogoUrl] = useState(currentLogo || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMode, setSelectedMode] = useState<'search' | 'upload'>(
    'search',
  );
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [uppy] = useState(() => {
    const uppy = new Uppy({
      restrictions: { maxNumberOfFiles: 1, allowedFileTypes: ['image/*'] },
    }).use(XHRUpload, {
      endpoint: `${API_URL}/api/logos/upload`,
      withCredentials: true,
    });

    uppy.on('complete', (result: any) => {
      openSnackbar({
        snackbarType: 'success',
        snackbarMessage: 'Οι αλλαγές σας έχουν αποθηκευτεί',
      });

      const logoUrl = result.successful?.[0]?.response?.body?.fullPath;

      if (external_id) {
        setNewLogo({ externalId: external_id, logoUrl });
      } else {
        setTeamLogo({ teamType, logoUrl });
      }
      closeModal();
    });

    return uppy;
  });

  useEffect(() => {
    fetchCollection({ search: searchQuery });
  }, [fetchCollection, searchQuery]);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchCollection({ search: e.target.value });
    }, 300);
  };

  const handleSelectLogo = (logoUrl: string, externalId: string) => {
    setNewLogoUrl(logoUrl);

    if (externalId) {
      setNewLogo({
        logoUrl,
        externalId,
      });
    } else {
      setTeamLogo({ teamType, logoUrl });
    }
    closeModal();
  };

  return (
    <ModalLayout
      title="Αλλαγή Logo"
      closeModal={closeModal}
      acceptText="Save"
      onCancel={closeModal}
      cancelText="Cancel"
    >
      <Stack spacing={3}>
        {newLogoUrl?.length > 0 && (
          <Box>
            <Typography variant="h4" sx={styles.title}>
              Τρέχον Logo
            </Typography>

            <Box
              component="img"
              src={newLogoUrl}
              alt="Current logo"
              sx={styles.logoPreview}
            />
          </Box>
        )}

        <Divider />

        <Stack direction="row" spacing={2}>
          <Button
            sx={styles.btn}
            variant={selectedMode === 'search' ? 'contained' : 'outlined'}
            onClick={() => setSelectedMode('search')}
          >
            Διάλεξε από τη βιβλιοθήκη
          </Button>

          <Button
            sx={styles.btn}
            variant={selectedMode === 'upload' ? 'contained' : 'outlined'}
            onClick={() => setSelectedMode('upload')}
          >
            Ανέβασε ένα νέο logo
          </Button>
        </Stack>

        {selectedMode === 'search' && (
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Αναζήτηση"
              value={searchQuery}
              onChange={handleSearch}
            />

            {!loading && (
              <Box>
                <Grid container spacing={2}>
                  {logos.map((logo: Logo) => (
                    <Grid
                      size={{ xs: 4, sm: 3, md: 2 }}
                      key={logo.name + logo.country}
                    >
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border:
                            newLogoUrl === logo.logo_url
                              ? '2px solid primary.main'
                              : '1px solid #ddd',
                        }}
                      >
                        <CardActionArea
                          onClick={() =>
                            handleSelectLogo(logo.logo_url, external_id)
                          }
                        >
                          <CardMedia
                            component="img"
                            height="80"
                            image={logo.logo_url!}
                            alt={logo.name || 'Logo'}
                            sx={{ objectFit: 'contain', p: 1 }}
                          />
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {logos.length === 0 && !loading && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    py={2}
                  >
                    {searchQuery
                      ? 'Δεν βρέθηκαν λογότυπα που να ταιριάζουν με την αναζήτηση.'
                      : 'Δεν υπάρχουν διαθέσιμα λογότυπα.'}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        )}

        {selectedMode === 'upload' && (
          <DashboardModal
            uppy={uppy}
            open
            proudlyDisplayPoweredByUppy={false}
            note="Κάνε κλικ για να ανεβάσεις ένα logo"
            onRequestClose={closeModal}
          />
        )}
      </Stack>
    </ModalLayout>
  );
};

const mapStateToProps = (state: RootState) => ({
  logos: selectLogoCollection(state),
  loading: selectLogosLoading(state),
  error: selectLogosError(state),
});

const connector = connect(mapStateToProps, {
  fetchCollection,
  setNewLogo,
  setTeamLogo,
  openModal,
  openSnackbar,
});

export default connector(EditLogoModal);
